import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Button,
  Card,
  Typography,
  Skeleton,
  Box,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Close, Search, SwapVert } from '@mui/icons-material';
import TablePagination from '@mui/material/TablePagination';
import { debounce } from '../../../utils/helper';

import { IToolBag } from '../../../utils/entities/interface';
import useSnackbar from '../../../hooks/useSnackbar';
import { AssignToolBag, GetToolBags } from '../../../services/tool';

const AssignToolBagModal = ({
  isOpen,
  onClose,
  requestId,
  onReload,
}: {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  onReload: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [toolBags, setToolBags] = useState<IToolBag[] | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [totalToolBags, setTotalToolBags] = useState(0);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const fetchToolBags = async (query = '') => {
    setLoading(true);
    try {
      const response = await GetToolBags({
        page: page + 1,
        limit: rowsPerPage,
        searchQuery: query,
      });
      setToolBags(response.data);
      setTotalToolBags(response.count);
    } catch (error) {
      console.error('Error fetching tool bags:', error);
    }
    setLoading(false);
  };

  const debouncedFetch = useCallback(debounce(fetchToolBags, 500), [
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (isOpen) {
      debouncedFetch(searchTerm);
    }
  }, [searchTerm, page, rowsPerPage, isOpen]);

  const handleAssign = async (
    event: React.MouseEvent<HTMLButtonElement>,
    toolBagId: string,
  ) => {
    event.preventDefault();

    try {
      const result = await AssignToolBag(requestId, toolBagId); // Make sure `requestId` is in scope
      if (result === 200) {
        showSnackbar('Tool Bag assigned successfully!', 'success');
        setTimeout(() => {
          onReload(); // Refresh the list or data
          onClose(); // Close modal or dialog
        }, 600);
      } else {
        showSnackbar('Assignment failed!', 'error');
      }
    } catch (error) {
      console.error('Error assigning tool bag:', error);
      showSnackbar('Error assigning tool bag.', 'error');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ borderRadius: 10, overflow: 'hidden' }}
      PaperProps={{ sx: { borderRadius: 10, overflow: 'hidden' } }}
    >
      <Box
        sx={{
          border: '1px solid #7B1FA2',
          borderRadius: 10,
          overflow: 'auto',
        }}
      >
        {SnackbarComponent}
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: '#181818',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
          }}
        >
          Assign Tool Bag
          <IconButton onClick={onClose} sx={{ color: 'gray' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ bgcolor: '#1E1E1E', color: 'white', padding: 3 }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <TextField
              variant="outlined"
              placeholder="Search Tool Bag"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#B0B0B0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '50%',
                input: { color: '#E0E0E0' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#1E1E1E',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#3A3A3A',
                  },
                  '&:hover fieldset': {
                    borderColor: '#555',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#777',
                  },
                },
              }}
            />

            <Button
              variant="outlined"
              startIcon={<SwapVert />}
              sx={{
                color: '#E0E0E0',
                borderColor: '#3A3A3A',
                bgcolor: '#1E1E1E',
                borderRadius: '8px',
                textTransform: 'none',
                paddingX: 2,
                '&:hover': {
                  borderColor: '#555',
                  backgroundColor: '#2A2A2A',
                },
              }}
            >
              Sort by
            </Button>
          </div>

          {loading ? (
            Array.from(new Array(5)).map((_, index) => (
              <Card
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#2A2A2A',
                  color: 'white',
                  padding: 2,
                  borderRadius: 2,
                  border: 'none',
                  mb: 1,
                }}
              >
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={150} height={15} />
                <Skeleton variant="text" width={80} height={15} />
                <Skeleton variant="rectangular" width={80} height={30} />
              </Card>
            ))
          ) : toolBags && toolBags.length > 0 ? (
            toolBags.map((toolBag) => (
              <Card
                key={toolBag._id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#2A2A2A',
                  color: 'white',
                  padding: 2,
                  borderRadius: 2,
                  border: 'none',
                  mb: 1,
                }}
              >
                <Typography variant="body1" width={'25%'}>
                  {toolBag.name}
                </Typography>
                <Typography variant="body2" color="gray" width={'45%'}>
                  {toolBag.description}
                </Typography>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={(e) => handleAssign(e, toolBag._id!)}
                >
                  Select
                </Button>
              </Card>
            ))
          ) : (
            <Typography
              color="gray"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                height: 300,
              }}
            >
              No Tool Bags Found.
            </Typography>
          )}

          <TablePagination
            rowsPerPageOptions={[2, 5, 10, 25, 50]}
            component="div"
            count={totalToolBags}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) =>
              setRowsPerPage(parseInt(event.target.value, 10))
            }
          />
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default AssignToolBagModal;
